import React, { useState, useRef, useCallback, useEffect } from 'react';
import SignUp from './auth/SignUp';
import SignIn from './auth/SignIn';

const Homepage = ({ onAuthenticated }) => {
  const [showSignUp, setShowSignUp] = useState(true);
  const [authHeight, setAuthHeight] = useState('auto');
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const authSectionRef = useRef(null);
  const authCardRef = useRef(null);

  const handleAuthSuccess = () => {
    // Scroll to top before starting onboarding
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onAuthenticated();
  };

  const scrollToAuthSection = useCallback(() => {
    if (authSectionRef.current) {
      try {
        // Check if smooth scrolling is supported
        if ('scrollBehavior' in document.documentElement.style) {
          // Get the element's position and dimensions
          const rect = authSectionRef.current.getBoundingClientRect();
          const elementTop = rect.top + window.pageYOffset;
          const elementHeight = rect.height;
          const windowHeight = window.innerHeight;
          
          // Mobile-specific positioning: show title prominently with just a sliver of card above
          let scrollTop;
          if (window.innerWidth <= 768) {
            // Mobile: position so title is visible with small sliver of card above
            // Adjust offset based on device orientation and size
            const mobileOffset = window.innerHeight <= 600 ? 30 : 40; // Smaller offset for very small screens
            scrollTop = Math.max(0, elementTop - mobileOffset);
          } else {
            // Desktop: center the element as before
            scrollTop = Math.max(0, elementTop - (windowHeight / 2) + (elementHeight / 2) - 20);
          }
          
          // Use smooth scrolling with precise positioning
          window.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        } else {
          // Fallback for browsers without smooth scrolling support
          const elementTop = authSectionRef.current.offsetTop;
          const elementHeight = authSectionRef.current.offsetHeight;
          const windowHeight = window.innerHeight;
          
          let scrollTop;
          if (window.innerWidth <= 768) {
            // Mobile: position so title is visible with small sliver of card above
            const mobileOffset = window.innerHeight <= 600 ? 30 : 40; // Smaller offset for very small screens
            scrollTop = Math.max(0, elementTop - mobileOffset);
          } else {
            // Desktop: center the element as before
            scrollTop = Math.max(0, elementTop - (windowHeight / 2) + (elementHeight / 2) - 20);
          }
          
          window.scrollTo({
            top: scrollTop,
            behavior: 'auto'
          });
        }
      } catch (error) {
        // Fallback to simple scroll if there's an error
        console.warn('Smooth scrolling failed, using fallback:', error);
        if (authSectionRef.current) {
          authSectionRef.current.scrollIntoView();
        }
      }
    }
  }, []);

  // Measure and set auth card height for smooth transitions
  const updateAuthHeight = useCallback(() => {
    if (authCardRef.current) {
      const newHeight = authCardRef.current.scrollHeight;
      setAuthHeight(`${newHeight}px`);
    }
  }, []);

  // Update height when form changes
  useEffect(() => {
    // Small delay to ensure DOM has updated
    const timer = setTimeout(updateAuthHeight, 50);
    return () => clearTimeout(timer);
  }, [showSignUp, updateAuthHeight]);

  // Focus first input when form changes (only when explicitly requested)
  useEffect(() => {
    if (shouldAutoFocus && authCardRef.current) {
      const firstInput = authCardRef.current.querySelector('input');
      if (firstInput) {
        firstInput.focus();
      }
      // Reset the flag after focusing
      setShouldAutoFocus(false);
    }
  }, [showSignUp, shouldAutoFocus]);

  const switchToSignIn = () => {
    setShowSignUp(false);
    setShouldAutoFocus(true);
    // Update height and scroll to auth section after switching
    requestAnimationFrame(() => {
      setTimeout(() => {
        updateAuthHeight();
        scrollToAuthSection();
      }, 50);
    });
  };

  const switchToSignUp = () => {
    setShowSignUp(true);
    setShouldAutoFocus(true);
    // Update height and scroll to auth section after switching
    requestAnimationFrame(() => {
      setTimeout(() => {
        updateAuthHeight();
        scrollToAuthSection();
      }, 50);
    });
  };

  return (
    <div className="homepage">
      <div className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">CryptoIndicators.ai</h1>
          <p className="hero-subtitle">Live cryptocurrency prices with AI-powered technical analysis</p>
          
          <div className="hero-features">
            <div className="feature-grid">
              <div className="feature-item">
                <div className="feature-icon">📊</div>
                <h3>Real-Time Data</h3>
                <p>Live cryptocurrency prices and market data from top exchanges</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🤖</div>
                <h3>AI Analysis</h3>
                <p>Advanced technical indicators and AI-powered market insights</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📈</div>
                <h3>Trading Tools</h3>
                <p>Professional-grade trading indicators and portfolio tracking</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">⚡</div>
                <h3>Instant Access</h3>
                <p>Get started in seconds with our intuitive dashboard</p>
              </div>
            </div>
          </div>

          <div className="hero-cta">
            <div className="cta-content">
              <h2>Your Free AI-Powered Crypto Dashboard Awaits</h2>
              <p>Instantly unlock real-time data, momentum signals, and custom AI trade insights.</p>
              
              <div className="cta-buttons">
                <button 
                  className={`cta-button ${showSignUp ? 'active' : ''}`}
                  onClick={switchToSignUp}
                >
                  Unlock Your Dashboard
                </button>
                <button 
                  className={`cta-button ${!showSignUp ? 'active' : ''}`}
                  onClick={switchToSignIn}
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-section" ref={authSectionRef}>
        <div 
          className="auth-container-fixed"
          style={{ height: authHeight }}
        >
          {showSignUp ? (
            <SignUp onSuccess={handleAuthSuccess} ref={authCardRef} />
          ) : (
            <SignIn onSuccess={handleAuthSuccess} onSwitchToSignUp={switchToSignUp} ref={authCardRef} />
        )}
        </div>
      </div>

      <div className="testimonials-section">
        <h2>What Our Users Say</h2>
        <div className="testimonials-grid">
          <div className="testimonial">
            <p>"The AI analysis is incredibly accurate. I've improved my trading significantly!"</p>
            <div className="testimonial-author">- Sarah M., Crypto Trader</div>
          </div>
          <div className="testimonial">
            <p>"Real-time data and professional indicators. Exactly what I needed."</p>
            <div className="testimonial-author">- Mike R., Day Trader</div>
          </div>
          <div className="testimonial">
            <p>"The interface is clean and the insights are valuable. Highly recommended!"</p>
            <div className="testimonial-author">- David L., Investor</div>
          </div>
        </div>
      </div>

      <footer className="footer">
        <div className="footer-cross-promotion">
          <p className="cross-promotion-text">
            💡 Looking for AI-powered stock insights too? 
            Check out our sister platform: <a href="https://stockindicators.ai" target="_blank" rel="noopener noreferrer" className="footer-link">StockIndicators.ai</a> for real-time analysis on your favorite stocks.
          </p>
        </div>
        <div className="footer-legal">
          <p>&copy; 2024 CryptoIndicators.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Homepage; 